// Platform abstraction: the web build uses browser APIs; the iOS app (Capacitor)
// registers a native implementation via setPlatform() before the app renders.
import type { Api } from './api';

export interface ShowNotification {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  onClick: () => void;
}

export interface Platform {
  name: 'web' | 'ios' | 'android';
  /** Ask the local OS/browser for notification permission. */
  requestNotificationPermission(): Promise<boolean>;
  notify(n: ShowNotification): void;
  /** Deliver a finished remote download to the user's device. */
  saveDownload(api: Api, sessionId: string, guid: string, filename: string): Promise<void>;
  /** Haptic tick (long-press etc.). */
  haptic(): void;
  /** Called when the app returns to foreground (for reconnect). */
  onResume(cb: () => void): () => void;
  /** Whether the server URL must be configured by the user (native apps). */
  needsServerUrl: boolean;
}

export const webPlatform: Platform = {
  name: 'web',
  needsServerUrl: false,
  async requestNotificationPermission() {
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    return (await Notification.requestPermission()) === 'granted';
  },
  notify(n) {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const note = new Notification(n.title, { body: n.body, icon: n.icon || undefined, tag: n.tag || undefined });
      note.onclick = () => {
        window.focus();
        n.onClick();
        note.close();
      };
    } catch {
      // Some mobile browsers only allow notifications from a service worker.
      navigator.serviceWorker?.getRegistration().then((r) => r?.showNotification(n.title, { body: n.body, icon: n.icon, tag: n.tag }));
    }
  },
  async saveDownload(api, sessionId, guid, filename) {
    const a = document.createElement('a');
    if (api.crossOrigin) {
      const blob = await api.downloadBlob(sessionId, guid);
      a.href = URL.createObjectURL(blob);
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
    } else {
      a.href = api.downloadUrl(sessionId, guid);
    }
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  },
  haptic() {
    navigator.vibrate?.(15);
  },
  onResume(cb) {
    const h = () => document.visibilityState === 'visible' && cb();
    document.addEventListener('visibilitychange', h);
    window.addEventListener('online', cb);
    return () => {
      document.removeEventListener('visibilitychange', h);
      window.removeEventListener('online', cb);
    };
  },
};

let current: Platform = webPlatform;
export const platform = () => current;
export const setPlatform = (p: Platform) => {
  current = p;
};
